import BigNumber from 'bignumber.js';
import { useMemo, useCallback, useEffect } from 'react';
import * as web3 from '@ywwwtseng/web3';
import { useAppUI } from '@ywwwtseng/react-kit';
import { useReadContract, useReadContracts } from '@ywwwtseng/wallet-kit/evm';
import { MOONINI_LOCK_ADDRESS, MOONINI_DECIMALS } from '@/constants';
import MOONINI_LOCK_ABI from '@/assets/abi/MooniniLock.abi.json';
import * as utils from '@/utils';

export interface Investment {
  assignedAmount: bigint;
  totalClaimableAmount: bigint;
  claimedAmount: bigint;
  lastClaimTime: bigint;
  investmentStartTime: bigint;
  investmentEndTime: bigint;
  isPendingOrEnded: boolean;
}

export function useInvestments(bscAddress: string | undefined) {
  const { showLoadingUI } = useAppUI();
  const { data: investmentLength } = useReadContract(!!bscAddress ? {
    address: MOONINI_LOCK_ADDRESS,
    abi: MOONINI_LOCK_ABI,
    functionName: 'getUserInvestmentLength',
    args: [bscAddress],
  } : undefined);

  const { data, refetch: refetchInvestments } = useReadContracts(!!investmentLength ? {
    contracts: [
      ...Array.from({ length: Number(investmentLength) }, (_, index) => ({
        address: MOONINI_LOCK_ADDRESS,
        abi: MOONINI_LOCK_ABI,
        functionName: 'getUserInvestment',
        args: [bscAddress, index],
      })),
    ],
  } : undefined);

  const { data: claimableAmount, refetch: refetchClaimableAmount } = useReadContract({
    address: MOONINI_LOCK_ADDRESS,
    abi: MOONINI_LOCK_ABI,
    functionName: 'calculateClaimableAmount',
    args: [bscAddress],
  });

  const refetch = useCallback(() => {
    refetchInvestments();
    refetchClaimableAmount();
  }, [refetchInvestments, refetchClaimableAmount]);


  const investments = useMemo(() => {
    if (investmentLength === 0n) return [];
    return data ? data.map((item: { result: Investment }) => item.result) : undefined;
  }, [data, investmentLength]);

  const totalClaimableAmount = useMemo(() => {
    return investments?.reduce((acc: BigNumber, item: Investment) => {
      if (!item) {
        return acc;
      };
      return acc.plus(new BigNumber(item.totalClaimableAmount || '0').minus(item.assignedAmount || '0'));
    }, new BigNumber(0)) || new BigNumber(0);
  }, [investments]);


  const formattedTotalClaimableAmount = useMemo(() => {
    return utils.token.displayAmount(web3.utils.formatUnits(totalClaimableAmount.toFixed(), MOONINI_DECIMALS), { showTinyAmount: false });
  }, [investments]);

  const claimedAmount = useMemo(() => {
    return investments?.reduce((acc: BigNumber, item: Investment) => {
      if (!item) {
        return acc;
      };

      return acc.plus(new BigNumber(item.claimedAmount || '0').minus(item.assignedAmount || '0'));
    }, new BigNumber(0)) || new BigNumber(0);
  }, [investments]);

  const formattedClaimedAmount = useMemo(() => {
    return utils.token.displayAmount(web3.utils.formatUnits(claimedAmount.toFixed(), MOONINI_DECIMALS), { showTinyAmount: false });
  }, [claimedAmount]);

  const formattedClaimableAmount = useMemo(() => {
    if (!claimableAmount) return '0';
    return utils.token.displayAmount(web3.utils.formatUnits(claimableAmount, MOONINI_DECIMALS), { showTinyAmount: false });
  }, [claimableAmount]);


  useEffect(() => {
    if (bscAddress) {
      showLoadingUI(!investments);
    }
  }, [bscAddress, investments]);

  return {
    investments,
    totalClaimableAmount,
    formattedTotalClaimableAmount,
    claimedAmount,
    formattedClaimedAmount,
    claimableAmount,
    formattedClaimableAmount,
    refetch,
  };
}